import axios from 'axios';
import { persistTokenIDB, readTokenIDB, clearTokenIDB } from './persistentStorage';
import { isNativePlatform } from './platform';

const API = process.env.REACT_APP_BACKEND_URL;

// Storage key for auth token (persists for 90 days aligned with backend session)
export const AUTH_TOKEN_KEY = 'ylm_session_token';

// Lazy-import Capacitor Preferences only on native to avoid bundling cost on web.
// IMPORTANT: registerPlugin() returns a Proxy that intercepts EVERY property
// access (including `.then`). If we return that Proxy from an async function,
// the JS Promise resolution detects a thenable and calls `.then()` on it — which
// throws "Preferences.then() is not implemented on ios". Wrap in an object.
async function getPreferences() {
  if (!isNativePlatform()) return null;
  try {
    const mod = await import('@capacitor/preferences');
    // Wrap in an object so the Proxy is never seen as a thenable
    return mod.Preferences ? { plugin: mod.Preferences } : null;
  } catch {
    return null;
  }
}

// In-memory mirror of the token. Filled by hydrateAuthFromIDB() at app boot
// and used as the synchronous source of truth for axios interceptor.
let memToken = null;

export function getAuthToken() {
  if (memToken) return memToken;
  try {
    return localStorage.getItem(AUTH_TOKEN_KEY);
  } catch {
    return null;
  }
}

export function setAuthToken(token) {
  memToken = token || null;
  try {
    if (token) {
      localStorage.setItem(AUTH_TOKEN_KEY, token);
    } else {
      localStorage.removeItem(AUTH_TOKEN_KEY);
    }
  } catch {
    // ignore storage errors (private mode)
  }
  // Mirror to IndexedDB (more resistant to iOS PWA storage eviction)
  if (token) {
    persistTokenIDB(token).catch(() => {});
  } else {
    clearTokenIDB().catch(() => {});
  }
  // Mirror to Capacitor Preferences on native (Keychain/EncryptedPrefs)
  getPreferences().then((wrap) => {
    const P = wrap?.plugin;
    if (!P) return;
    if (token) P.set({ key: AUTH_TOKEN_KEY, value: token }).catch(() => {});
    else P.remove({ key: AUTH_TOKEN_KEY }).catch(() => {});
  });
}

export function clearAuthToken() {
  memToken = null;
  try {
    localStorage.removeItem(AUTH_TOKEN_KEY);
    sessionStorage.removeItem('_auth_bridge');
  } catch {
    // ignore
  }
  clearTokenIDB().catch(() => {});
  getPreferences().then((wrap) => {
    const P = wrap?.plugin;
    if (P) P.remove({ key: AUTH_TOKEN_KEY }).catch(() => {});
  });
}

// Hydrate the auth token from the most reliable backend available.
// Order on native: localStorage (instant) > Capacitor Preferences (Keychain) > IndexedDB
// Order on web:    localStorage > IndexedDB
//
// IMPORTANT (2026-05-10 fix): On a real iPhone the Capacitor Preferences plugin
// can take 2-3 seconds to lazy-load + read from Keychain at first launch. The
// previous 1500ms global timeout caused the function to return null before
// Preferences could answer, so the user was logged out at every cold start.
// We now check localStorage SYNCHRONOUSLY first (instant), and only fall back
// to Preferences/IDB if localStorage is empty, with a much more generous timeout.
export async function hydrateAuthFromIDB() {
  // Fast path 1: localStorage is synchronous and survives app restarts in Capacitor WebView.
  try {
    const ls = localStorage.getItem(AUTH_TOKEN_KEY);
    if (ls) {
      memToken = ls;
      // Mirror to other backends in background (no await — already have token)
      persistTokenIDB(ls).catch(() => {});
      if (isNativePlatform()) {
        getPreferences().then((wrap) => {
          const P = wrap?.plugin;
          if (P) P.set({ key: AUTH_TOKEN_KEY, value: ls }).catch(() => {});
        });
      }
      return ls;
    }
  } catch { /* ignore */ }

  // Fast path 2: short-lived race, longer timeout (4s) to give Keychain time on first launch.
  const timeout = new Promise((resolve) => setTimeout(() => resolve(null), 4000));
  const work = (async () => {
    try {
      // 1. Native: try Preferences (Keychain on iOS, EncryptedSharedPreferences on Android)
      if (isNativePlatform()) {
        const wrap = await getPreferences();
        const P = wrap?.plugin;
        if (P) {
          const { value } = await P.get({ key: AUTH_TOKEN_KEY });
          if (value) {
            memToken = value;
            try { localStorage.setItem(AUTH_TOKEN_KEY, value); } catch { /* ignore */ }
            persistTokenIDB(value).catch(() => {});
            return value;
          }
        }
      }
      // 2. IndexedDB
      const idbToken = await readTokenIDB();
      if (idbToken) {
        memToken = idbToken;
        try { localStorage.setItem(AUTH_TOKEN_KEY, idbToken); } catch { /* ignore */ }
        if (isNativePlatform()) {
          getPreferences().then((wrap) => {
            const P = wrap?.plugin;
            if (P) P.set({ key: AUTH_TOKEN_KEY, value: idbToken }).catch(() => {});
          });
        }
        return idbToken;
      }
    } catch {
      // ignore
    }
    return null;
  })();
  return Promise.race([work, timeout]);
}

const api = axios.create({
  baseURL: `${API}/api`,
  withCredentials: true,
});

// Flag to suppress 401 redirects during auth verification
let suppressAuthRedirect = false;

export function setSuppressAuthRedirect(value) {
  suppressAuthRedirect = value;
}

// Request interceptor: attach Bearer token from localStorage as primary auth method.
// This bypasses Chrome mobile / Kubernetes ingress SameSite cookie issues.
// The httpOnly cookie (set by backend) still works as a secondary fallback.
api.interceptors.request.use((config) => {
  const token = getAuthToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Response interceptor: handle auth errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401 && !suppressAuthRedirect) {
      // Clear stale token
      clearAuthToken();
      const currentPath = window.location.pathname;
      // Public routes that should NOT redirect to /login on a 401 from
      // background calls (e.g. the auth-check on landing pages).
      const isPublicRoute =
        currentPath === '/login' ||
        currentPath === '/' ||
        currentPath === '/en' ||
        currentPath === '/privacy' ||
        currentPath === '/privacy-beta' ||
        currentPath === '/termini' ||
        currentPath.includes('/auth/callback');
      if (!isPublicRoute) {
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

export default api;
